import { useState } from "react";
import Router from "next/router";

import { useMe } from "../../me";
import { useDispatchFlashMessage } from "../../flashMessage";
import { acceptMarketplaceOffer } from "../../services/marketplaceService";
import { Resource } from "../../api/useFetch";
import { Offer } from "../offer";
import { ShiftOffer } from "../shiftOffer";
import { MessageType } from "../../flashMessage";
// @ts-ignore
import { offers } from "../../routes";

type FailureReason =
  | "already_accepted"
  | "offer_canceled"
  | "price_mismatch"
  | "unknown";

type AcceptResult = {
  success: boolean;
  reason?: FailureReason;
};

type AcceptOfferResult = {
  acceptOffer: (offer: Resource<Offer>) => Promise<AcceptResult>;
  acceptShift: (shift: Resource<ShiftOffer>) => Promise<AcceptResult>;
  isAccepting: boolean;
};

function convertPriceToMinorUnits(price: string): number {
  return Math.round(parseFloat(price) * 100);
}

export function useAcceptOffer(): AcceptOfferResult {
  const [isAccepting, setIsAccepting] = useState(false);
  const me = useMe();
  const dispatchFlashMessage = useDispatchFlashMessage();

  const acceptOffer = async (offer: Resource<Offer>): Promise<AcceptResult> => {
    if (!me.id || !me.roles || me.roles.length === 0) {
      dispatchFlashMessage("failure", "shared.unknownError");
      return { success: false, reason: "unknown" };
    }

    if (!offer.attributes.currency) {
      dispatchFlashMessage("failure", "shared.unknownError");
      return { success: false, reason: "unknown" };
    }

    setIsAccepting(true);

    try {
      // Marketplace API currently only supports prebooked offers
      const priceInMinorUnits = convertPriceToMinorUnits(
        offer.attributes.price,
      );
      await acceptMarketplaceOffer({
        offer_uuid: offer.id,
        currency: offer.attributes.currency,
        price: parseFloat(offer.attributes.price),
        price_minor_unit: priceInMinorUnits,
        type: "prebooked",
        user_id: me.id,
        user_roles: me.roles,
        user_lsp_id: me.lsp?.id || "",
        user_bd_id: me.lsp?.businessDistrict?.uuid || "",
      });

      dispatchFlashMessage("success", "offer.accepted");

      const { href, as } = offers();
      Router.push(href, as).catch(() => {
        // Silently ignore navigation abort errors (e.g., component unmount during navigation)
      });

      return { success: true };
    } catch (error) {
      return handleAcceptError(error, dispatchFlashMessage);
    } finally {
      setIsAccepting(false);
    }
  };

  const acceptShift = async (
    shift: Resource<ShiftOffer>,
  ): Promise<AcceptResult> => {
    if (!me.id || !me.roles || me.roles.length === 0) {
      dispatchFlashMessage("failure", "shared.unknownError");
      return { success: false, reason: "unknown" };
    }

    if (!shift.attributes.currency) {
      dispatchFlashMessage("failure", "shared.unknownError");
      return { success: false, reason: "unknown" };
    }

    setIsAccepting(true);

    try {
      const priceInMinorUnits = convertPriceToMinorUnits(
        shift.attributes.price,
      );
      await acceptMarketplaceOffer({
        offer_uuid: shift.id,
        currency: shift.attributes.currency,
        price: parseFloat(shift.attributes.price),
        price_minor_unit: priceInMinorUnits,
        type: "shift",
        user_id: me.id,
        user_roles: me.roles,
        user_lsp_id: me.lsp?.id || "",
        user_bd_id: me.lsp?.businessDistrict?.uuid || "",
      });

      dispatchFlashMessage("success", "offer.accepted");

      const { href, as } = offers();
      Router.push(href, as).catch(() => {
        // Silently ignore navigation abort errors (e.g., component unmount during navigation)
      });

      return { success: true };
    } catch (error) {
      return handleAcceptError(error, dispatchFlashMessage);
    } finally {
      setIsAccepting(false);
    }
  };

  return { acceptOffer, acceptShift, isAccepting };
}

function handleAcceptError(
  error: unknown,
  dispatchFlashMessage: (
    messageType: MessageType,
    messageId: string,
    values?: object,
  ) => void,
): AcceptResult {
  const errorWithStatus = error as { status?: number };
  const status = errorWithStatus.status;

  let reason: FailureReason = "unknown";

  // Marketplace API uses standard HTTP status codes:
  // 409: already accepted, 422: price mismatch, 404: not found.
  if (status === 409) {
    dispatchFlashMessage("failure", "offer.failure.already_accepted");
    reason = "already_accepted";
  } else if (status === 422) {
    dispatchFlashMessage("failure", "offer.failure.price_mismatch");
    reason = "price_mismatch";
  } else if (status === 404) {
    dispatchFlashMessage("failure", "offer.failure.offer_canceled");
    reason = "offer_canceled";
  } else {
    dispatchFlashMessage("failure", "shared.unknownError");
  }

  return { success: false, reason };
}
